import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

export default function AdminBroadcastPage() {
  const [current, setCurrent] = useState(null);
  const [message, setMessage] = useState("");
  const [style, setStyle] = useState("info");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/broadcast/current");
      setCurrent(data || null);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load broadcast"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { message, style });
      setCurrent(data.broadcast);
      setMessage("");
      toast.success("Broadcast published");
    } catch (err) {
      toast.error(formatApiError(err, "Publish failed"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/broadcast");
      setCurrent(null);
      toast.success("Broadcast deactivated");
    } catch (err) {
      toast.error(formatApiError(err, "Deactivate failed"));
    } finally {
      setBusy(false);
    }
  };

  const styleClass = {
    info: "bg-brand-navy/10 text-brand-navy border-brand-navy/20",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    success: "bg-brand-green/10 text-brand-green border-brand-green/20",
  }[style];

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-12 md:px-10" data-testid="admin-broadcast-page">
      <div className="mb-rise">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
          <Megaphone className="h-3 w-3" />
          System broadcast
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Announce to all brokers
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-500">
          A single active banner shows at the top of every broker dashboard.
          Publishing replaces the previous one; users can dismiss per banner.
        </p>
      </div>

      {current && (
        <div
          className="mt-8 rounded-2xl border border-gray-100 bg-white p-5 shadow-mb-card"
          data-testid="broadcast-current-card"
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Currently live
          </div>
          <div className={`mt-2 rounded-xl border px-4 py-3 text-sm ${{
            info: "bg-brand-navy/8 border-brand-navy/20 text-brand-navy",
            warning: "bg-amber-50 border-amber-200 text-amber-800",
            success: "bg-brand-green/10 border-brand-green/25 text-brand-green",
          }[current.style] || ""}`}>
            {current.message}
          </div>
          <div className="mt-3 text-xs text-gray-500">
            style {current.style} · published {new Date(current.created_at).toLocaleString()}
          </div>
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={deactivate}
              disabled={busy}
              data-testid="broadcast-deactivate"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Deactivate
            </Button>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
          {current ? "Publish new (replaces current)" : "Publish a broadcast"}
        </h3>
        <div className="mt-3 grid gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500">Style</label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="w-40" data-testid="broadcast-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info · navy</SelectItem>
                <SelectItem value="warning">Warning · amber</SelectItem>
                <SelectItem value="success">Success · green</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500">Message ({message.length}/200)</label>
            <Textarea
              rows={3}
              maxLength={200}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Maintenance window tonight 10-11pm PST…"
              data-testid="broadcast-message"
            />
            {message && (
              <div className={`mt-3 rounded-xl border px-4 py-3 text-sm ${styleClass}`} data-testid="broadcast-preview">
                {message}
              </div>
            )}
          </div>
          <Button onClick={publish} disabled={busy || !message.trim()} data-testid="broadcast-publish">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Publish
          </Button>
        </div>
      </div>
    </div>
  );
}
